import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import TxnList from "./TxnList";
import useUniversalStore from "store/universalStore";
import ExpandableButton from "components/ExpandableButton";
import ReportsView from "./ReportsView";
import { debounce } from "utils";

function Home() {
  const { write, selectedMonth, txnViewMode, setTxnViewMode } = useUniversalStore((state) => state);
  const [animation, setAnimation] = useState("");
  const [hideCancelled, setHideCancelled] = useState(true);
  const [reportLoading, setReportLoading] = useState({ pdf: false, excel: false });
  const isToday = dayjs(selectedMonth).isSame(dayjs(), "day");
  const [selectedDate, setSelectedDate] = useState(selectedMonth);

  useEffect(() => {
    write({ selectedMonth: new Date() });
  }, []);

  useEffect(() => {
    setSelectedDate(selectedMonth);
  }, [selectedMonth]);

  const prevDate = async () => {
    const newDate = dayjs(selectedMonth).subtract(1, "days").toDate();
    write({ selectedMonth: newDate });
    setSelectedDate(newDate);
    setAnimation("animate__animated animate__slideInRight fast-animation");
  };

  const nextDate = async () => {
    if (isToday) return;
    const newDate = dayjs(selectedMonth).add(1, "days").toDate();
    write({ selectedMonth: newDate });
    setSelectedDate(newDate);
    setAnimation("animate__animated animate__slideInLeft fast-animation");
  };

  const debouncedWrite = useMemo(
    () => debounce((value) => write({ selectedMonth: value }), 500),
    [write]
  );

  const handleDateChange = (value) => {
    setSelectedDate(value);
    if (value) debouncedWrite(value);
  };

  return (
    <div className="background">
      <div className="row py-2 g-0 align-items-center border-bottom">
        <div className="col-2 col-md-1 d-flex justify-content-center">
          <i className="bi bi-arrow-left-circle-fill fs-3 fs-md-2 border-0 p-1 text-app cursor-pointer"
            onClick={prevDate}
          />
        </div>
        <div className="col-8 col-md-10 my-1 d-flex gap-2 justify-content-center align-items-center flex-wrap">
          <div className="datepicker-container d-flex align-items-center">
            <DatePicker
              selected={selectedDate ? new Date(selectedDate) : new Date()}
              onChange={handleDateChange}
              dateFormat={["dd-MMM-yyyy", "dd-MM-yyyy", "dd/MM/yyyy", "yyyy-MM-dd", "yyyy"]}
              className="bg-app p-1 fs-6 fs-md-5 text-center w-auto text-white border-1 shadow-none rounded-2"
              showIcon
              maxDate={new Date()}
              showYearDropdown
              calendarClassName="hide-header"
              yearDropdownItemNumber={2}
              showMonthDropdown
              icon={<i className="bi bi-calendar-date text-white py-1 fs-6 fs-md-5"></i>}
            />
          </div>

          <div className="btn-group btn-group-sm" role="group" aria-label="Cashbook View Mode">
            <button
              type="button"
              className={`btn ${txnViewMode === "t-view" ? "btn-app" : "btn-outline-app"}`}
              onClick={() => setTxnViewMode?.("t-view")}
              title="Khata (2-column layout)"
            >
              <i className="bi bi-layout-split me-1 d-inline d-sm-none" />
              <span className="d-none d-sm-inline">Khata</span>
            </button>
            <button
              type="button"
              className={`btn ${txnViewMode === "table" ? "btn-app" : "btn-outline-app"}`}
              onClick={() => setTxnViewMode?.("table")}
              title="Statement Table View"
            >
              <i className="bi bi-table me-1 d-inline d-sm-none" />
              <span className="d-none d-sm-inline">Statement</span>
            </button>
          </div>

          <i className={`fs-3 fs-md-2 text-app cursor-pointer 
              ${hideCancelled ? "bi-filter-circle-fill" : "bi-filter"}`}
            onClick={() => setHideCancelled(!hideCancelled)}></i>

          <div className="dropdown p-0">
            <div className="btn btn-sm p-0"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false">
              {reportLoading.pdf ?
                <span className="spinner-border text-app" style={{ width: '1.5rem', height: '1.5rem' }} role="status" aria-hidden="true" />
                : <i className="bi bi-file-pdf fs-2 text-app" />
              }
            </div>
            <ReportsView setReportLoading={setReportLoading} type="pdf" />
          </div>

          <div className="dropdown p-0">
            <div className="btn btn-sm p-0"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false">
              {reportLoading.excel ?
                <span className="spinner-border text-app" style={{ width: '1.5rem', height: '1.5rem' }} role="status" aria-hidden="true" />
                : <i className="bi bi-filetype-xls fs-2 text-app" />
              }
            </div>
            <ReportsView setReportLoading={setReportLoading} type="excel" />
          </div>

        </div>
        <div className="col-2 col-md-1 d-flex justify-content-center" align="right">
          <i className={`bi fs-3 fs-md-2 text-app p-1 ${!isToday ? "bi-arrow-right-circle-fill cursor-pointer" : "bi-arrow-right-circle opacity-50"}`}
            onClick={nextDate}
          />
        </div>
      </div>

      <TxnList
        animation={animation}
        setAnimation={setAnimation}
        hideClosed={hideCancelled}
        handleToggle={(value) => setHideCancelled(value)} />

      <div className="addTxn-btn" style={{ bottom: "90px" }}>
        <ExpandableButton
          title="New Entry"
          formId="newTxn"
          // onClick={(e) => goToNextStep()}
          icon="bi bi-pencil-fill"
        />
      </div>
    </div>
  );
}

export default Home;
